import { Tag, TagGroup } from "@heroui/react";
import { useEffect, useState } from "react";

import { ComboField } from "@/components/kit";
import { useDebounce } from "@/hooks/useDebounce";

export interface TagFilterItem {
  id: string;
  name: string;
}

/**
 * A filter whose options come from a searchable list on the server (thousands of scraped categories, brands or
 * products): type to search, pick to add; the chosen ones stay below as removable tags.
 */
export function AsyncTagFilter({
  label,
  placeholder,
  selected,
  onChange,
  fetchItems,
}: {
  label: string;
  placeholder: string;
  selected: TagFilterItem[];
  onChange: (items: TagFilterItem[]) => void;
  fetchItems: (params: {
    name?: string;
    page: number;
    limit: number;
  }) => Promise<{ data: TagFilterItem[]; totalPages: number }>;
}) {
  const [input, setInput] = useState("");
  const debounced = useDebounce(input.trim(), 300);
  const [items, setItems] = useState<TagFilterItem[]>([]);

  useEffect(() => {
    let live = true;

    fetchItems({ name: debounced || undefined, page: 1, limit: 20 })
      .then((result) => live && setItems(result.data))
      .catch(() => live && setItems([]));

    return () => {
      live = false;
    };
  }, [debounced, fetchItems]);

  const options = items.filter(
    (item) => !selected.some((chosen) => chosen.id === item.id),
  );

  return (
    <div className="flex flex-col gap-2">
      {/* Remounted after each pick so the search box starts empty again. */}
      <ComboField
        key={selected.length}
        disableFilter
        emptyText="No matches"
        inputValue={input}
        items={options}
        label={label}
        placeholder={placeholder}
        value=""
        onChange={(id) => {
          const item = items.find((candidate) => candidate.id === id);

          setInput("");
          if (item) onChange([...selected, item]);
        }}
        onInputChange={setInput}
      />
      {selected.length > 0 ? (
        <TagGroup
          aria-label={`Selected ${label.toLowerCase()}`}
          onRemove={(keys) =>
            onChange(selected.filter((item) => !keys.has(item.id)))
          }
        >
          <TagGroup.List className="flex flex-wrap gap-1.5">
            {selected.map((item) => (
              <Tag key={item.id} id={item.id} textValue={item.name}>
                {item.name}
                <Tag.RemoveButton />
              </Tag>
            ))}
          </TagGroup.List>
        </TagGroup>
      ) : null}
    </div>
  );
}
